"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useLiveBroadcasts } from "@/contexts/live-broadcast-context";
import { intercomBooted } from "@/components/ui/intercom/open-support-chat";
import { renderNothingIfChunkFails } from "@/lib/optional-chunk";

const PostHogProvider = dynamic<
  import("@/components/PostHogProvider").PostHogProviderProps
>(
  () => renderNothingIfChunkFails(import("@/components/PostHogProvider").then((m) => m.PostHogProvider)),
  { ssr: false },
);

const IntercomProvider = dynamic(
  () =>
    renderNothingIfChunkFails(
      import("@/components/ui/intercom/IntercomProvider").then((m) => m.IntercomProvider),
    ),
  { ssr: false }
);

const LiveBroadcastWidget = dynamic(
  () =>
    renderNothingIfChunkFails(
      import("@/components/live/LiveBroadcastWidget").then((m) => m.LiveBroadcastWidget),
    ),
  { ssr: false }
);

const EmailCapturePanel = dynamic(
  () =>
    renderNothingIfChunkFails(
      import("@/components/marketing/EmailCapturePanel").then((m) => m.EmailCapturePanel),
    ),
  { ssr: false },
);

export function DeferredEmailCapturePanel() {
  return <EmailCapturePanel />;
}

interface PostHogBootProps {
  pathname?: string | null;
}

export function PostHogBoot({ pathname }: PostHogBootProps = {}) {
  if (process.env.NODE_ENV === "development") return null;
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return null;
  return <PostHogProvider pathname={pathname ?? undefined} />;
}

export function DeferredPostHog(props: PostHogBootProps = {}) {
  return <PostHogBoot {...props} />;
}

function useBootDelay(bootDelayMs?: number): boolean {
  const [ready, setReady] = useState(bootDelayMs == null);

  useEffect(() => {
    if (bootDelayMs == null) return;
    const start = () => {
      const timer = window.setTimeout(() => setReady(true), bootDelayMs);
      return () => window.clearTimeout(timer);
    };
    if (document.readyState === "complete") {
      return start();
    }
    let cleanup: (() => void) | undefined;
    const handleLoad = () => {
      cleanup = start();
    };
    window.addEventListener("load", handleLoad, { once: true });
    return () => {
      window.removeEventListener("load", handleLoad);
      cleanup?.();
    };
  }, [bootDelayMs]);

  return ready;
}

export function DeferredIntercomProvider({ bootDelayMs }: { bootDelayMs?: number } = {}) {
  const [bootedElsewhere] = useState(() => typeof window !== "undefined" && intercomBooted());
  const ready = useBootDelay(bootedElsewhere ? undefined : bootDelayMs);

  if (!process.env.NEXT_PUBLIC_INTERCOM_APP_ID) return null;
  if (!ready) return null;
  return <IntercomProvider />;
}

const XPixel = dynamic(
  () => renderNothingIfChunkFails(import("@/components/analytics/XPixel").then((m) => m.XPixel)),
  { ssr: false }
);

export function DeferredXPixel({ bootDelayMs }: { bootDelayMs?: number } = {}) {
  const ready = useBootDelay(bootDelayMs);
  if (!ready) return null;
  return <XPixel />;
}

export function DeferredLiveBroadcastWidget() {
  const pathname = usePathname();
  const { isLive, stage } = useLiveBroadcasts();
  if (pathname === "/pricing") return null;
  if (!isLive || stage === "hidden") return null;
  return <LiveBroadcastWidget />;
}
